import { API_CONFIG } from '../config/api';
import AIService from './AIService';
import { trackEvent } from '../utils/analytics';
import { apiFetch, buildResponseError, parseResponseBody } from '../utils/http';

// Create a simple API utility for campaign service
const createApiCall = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    data = null,
    headers = {},
    requireAuth = true
  } = options;

  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  const response = await apiFetch(url, {
    method,
    headers,
    data,
    credentials: requireAuth ? 'include' : 'include'
  });
  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    throw buildResponseError(response, responseBody);
  }

  return responseBody ?? {};
};

// Campaign Service for managing campaign-related API calls
class CampaignService {
  constructor() {
    // Removed: this.logInfo('CampaignService initialized');
  }

  // Logging utility
  logInfo(message, data = null) {
    // Removed for production - only keep for development debugging if needed
    // console.log(`[CampaignService] ${message}`, data || '');
  }

  logError(message, error = null) {
    console.error(`[CampaignService] ERROR: ${message}`, error || '');
  }

  /**
   * Create a new campaign
   * @param {Object|FormData} campaignData - Campaign data object or FormData with files
   * @param {Object} options - Additional options (isFormData, etc.)
   * @returns {Promise<Object>} Created campaign data
   */
  async createCampaign(campaignData, options = {}) {
    // Removed: this.logInfo('Creating campaign', campaignData);

    try {
      if (options.isFormData) {
        // Handle FormData for file uploads
        const response = await this.createCampaignWithFiles(campaignData);
        trackEvent('campaign_create', {
          has_files: true
        });
        // Removed: this.logInfo('Campaign with files created successfully', response);
        return response;
      } else {
        // Handle regular JSON data
        const response = await createApiCall(API_CONFIG.ENDPOINTS.CAMPAIGNS.CREATE, {
          method: 'POST',
          data: campaignData
        });
        trackEvent('campaign_create', {
          has_files: false
        });
        // Removed: this.logInfo('Campaign created successfully', response);
        return response;
      }
    } catch (error) {
      this.logError('Failed to create campaign', error);
      throw error;
    }
  }

  /**
   * Create campaign with file uploads
   * @param {FormData} formData - FormData containing campaign data and files
   * @returns {Promise<Object>} Created campaign data
   */
  async createCampaignWithFiles(formData) {
    // Removed: this.logInfo('Creating campaign with files');

    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CAMPAIGNS.CREATE}`;

      // Debug: Log FormData contents - Removed for production
      // this.logInfo('FormData contents:');
      // for (let [key, value] of formData.entries()) {
      //   if (value instanceof File) {
      //     this.logInfo(`${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
      //   } else {
      //     this.logInfo(`${key}: ${value}`);
      //   }
      // }
      
      const response = await apiFetch(url, {
        method: 'POST',
        data: formData,
        isFormData: true
      });
      const responseBody = await parseResponseBody(response);

      if (!response.ok) {
        throw buildResponseError(response, responseBody);
      }

      return responseBody ?? {};
    } catch (error) {
      this.logError('Failed to create campaign with files', error);
      throw error;
    }
  }

  /**
   * Get list of campaigns
   * @param {Object} filters - Filter options (status, limit, offset)
   * @returns {Promise<Array>} List of campaigns
   */
  async listCampaigns(filters = {}) {
    // Removed: this.logInfo('Fetching campaigns list', filters);

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.offset) queryParams.append('offset', filters.offset);

      const endpoint = `${API_CONFIG.ENDPOINTS.CAMPAIGNS.LIST}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await createApiCall(endpoint);
      
      // Removed: this.logInfo('Campaigns list fetched successfully', response);
      return response.campaigns || [];
    } catch (error) {
      this.logError('Failed to fetch campaigns list', error);
      throw error;
    }
  }

  /**
   * Get campaign details by ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign details
   */
  async getCampaign(campaignId) {
    // Removed: this.logInfo('Fetching campaign details', { campaignId });

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.CAMPAIGNS.GET(campaignId));
      // Removed: this.logInfo('Campaign details fetched successfully', response);
      return response.campaign || response;
    } catch (error) {
      this.logError('Failed to fetch campaign details', error);
      throw error;
    }
  }

  /**
   * Update campaign information
   * @param {string} campaignId - Campaign ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated campaign data
   */
  async updateCampaign(campaignId, updateData) {
    // Removed: this.logInfo('Updating campaign', { campaignId, updateData });

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.CAMPAIGNS.UPDATE(campaignId), {
        method: 'PUT',
        data: updateData
      });
      // Removed: this.logInfo('Campaign updated successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to update campaign', error);
      throw error;
    }
  }

  /**
   * Generate posts for campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} generationOptions - Generation options (count, content_type)
   * @returns {Promise<Object>} Generated posts response
   */
  async generatePosts(campaignId, generationOptions = {}) {
    // Removed: this.logInfo('Delegating post generation to AIService', { campaignId, generationOptions });
    return AIService.generatePosts(campaignId, generationOptions);
  }

  /**
   * Generate posts for campaign (alias for generatePosts)
   * @param {string} campaignId - Campaign ID
   * @param {Object} generationOptions - Generation options (count, content_type)
   * @returns {Promise<Object>} Generated posts response
   */
  async generatePostsForCampaign(campaignId, generationOptions = {}) {
    return this.generatePosts(campaignId, generationOptions);
  }

  /**
   * Generate or regenerate strategy for campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} strategyOptions - Strategy generation options
   * @returns {Promise<Object>} Generated strategy response
   */
  async generateStrategy(campaignId, strategyOptions = {}) {
    // Removed: this.logInfo('Delegating strategy generation to AIService', { campaignId, strategyOptions });
    return AIService.generateStrategy(campaignId, strategyOptions);
  }

  /**
   * Regenerate a specific week's strategy
   * @param {string} campaignId - Campaign ID
   * @param {number} weekIndex - Week index (0-based)
   * @param {Object} regenerateOptions - Regeneration options
   * @returns {Promise<Object>} Regenerated week response
   */
  async regenerateWeekStrategy(campaignId, weekIndex, regenerateOptions = {}) {
    // Removed: this.logInfo('Regenerating week strategy', { campaignId, weekIndex, regenerateOptions });

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.AI.STRATEGY_WEEK(campaignId, weekIndex), {
        method: 'POST',
        data: regenerateOptions
      });
      // Removed: this.logInfo('Week strategy regenerated successfully', response);
      trackEvent('strategy_regenerate', {
        week_index: weekIndex
      });
      return response;
    } catch (error) {
      this.logError('Error regenerating week strategy', error);
      throw error;
    }
  }

  /**
   * Update campaign strategy and other fields
   * @param {string} campaignId - Campaign ID
   * @param {Object} updateData - Data to update (strategy, name, etc.)
   * @returns {Promise<Object>} Updated campaign response
   */
  async updateCampaignStrategy(campaignId, updateData) {
    // Removed: this.logInfo('Updating campaign strategy and data', { campaignId, updateData });

    try {
      const response = await this.updateCampaign(campaignId, updateData);
      // Removed: this.logInfo('Campaign strategy updated successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to update campaign strategy', error);
      throw error;
    }
  }

  /**
   * Delete campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteCampaign(campaignId) {
    // Removed: this.logInfo('Deleting campaign', { campaignId });

    try {
      const response = await createApiCall(API_CONFIG.ENDPOINTS.CAMPAIGNS.DELETE(campaignId), {
        method: 'DELETE'
      });
      // Removed: this.logInfo('Campaign deleted successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to delete campaign', error);
      throw error;
    }
  }

  /**
   * Get campaign analytics
   * @param {string} campaignId - Campaign ID
   * @param {Object} analyticsOptions - Analytics options (start_date, end_date, metrics)
   * @returns {Promise<Object>} Campaign analytics data
   */
  async getCampaignAnalytics(campaignId, analyticsOptions = {}) {
    // Removed: this.logInfo('Fetching campaign analytics', { campaignId, analyticsOptions });

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (analyticsOptions.start_date) queryParams.append('start_date', analyticsOptions.start_date);
      if (analyticsOptions.end_date) queryParams.append('end_date', analyticsOptions.end_date);
      if (analyticsOptions.metrics) queryParams.append('metrics', analyticsOptions.metrics);

      const endpoint = `${API_CONFIG.ENDPOINTS.ANALYTICS.CAMPAIGNS(campaignId)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await createApiCall(endpoint);
      
      // Removed: this.logInfo('Campaign analytics fetched successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to fetch campaign analytics', error);
      throw error;
    }
  }

  /**
   * Start/activate campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign activation response
   */
  async activateCampaign(campaignId) {
    // Removed: this.logInfo('Activating campaign', { campaignId });

    try {
      const response = await this.updateCampaign(campaignId, { status: 'active' });
      // Removed: this.logInfo('Campaign activated successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to activate campaign', error);
      throw error;
    }
  }

  /**
   * Pause campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign pause response
   */
  async pauseCampaign(campaignId) {
    // Removed: this.logInfo('Pausing campaign', { campaignId });

    try {
      const response = await this.updateCampaign(campaignId, { status: 'paused' });
      // Removed: this.logInfo('Campaign paused successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to pause campaign', error);
      throw error;
    }
  }

  /**
   * Complete campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign completion response
   */
  async completeCampaign(campaignId) {
    // Removed: this.logInfo('Completing campaign', { campaignId });

    try {
      const response = await this.updateCampaign(campaignId, { status: 'completed' });
      // Removed: this.logInfo('Campaign completed successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to complete campaign', error);
      throw error;
    }
  }

  /**
   * Duplicate campaign
   * @param {string} campaignId - Campaign ID to duplicate
   * @param {Object} overrides - Properties to override in the duplicate
   * @returns {Promise<Object>} Duplicated campaign data
   */
  async duplicateCampaign(campaignId, overrides = {}) {
    // Removed: this.logInfo('Duplicating campaign', { campaignId, overrides });

    try {
      // First get the original campaign
      const originalCampaign = await this.getCampaign(campaignId);
      
      // Create new campaign data with overrides
      const duplicateData = {
        ...originalCampaign,
        name: `${originalCampaign.name} (Copy)`,
        status: 'draft',
        ...overrides
      };

      // Remove fields that shouldn't be duplicated
      delete duplicateData.id;
      delete duplicateData.created_at;
      delete duplicateData.updated_at;

      const response = await this.createCampaign(duplicateData);
      // Removed: this.logInfo('Campaign duplicated successfully', response);
      return response;
    } catch (error) {
      this.logError('Failed to duplicate campaign', error);
      throw error;
    }
  }
}

// Create singleton instance
const campaignService = new CampaignService();

export default campaignService;
